import { Thumbnail } from '@/components/base';
import { Button } from '@/components/ui';
import { setBreadcrumbs, useInfiniteScroll } from '@/hooks';
import { trans } from '@/i18n';
import { ROUTE_PATH } from '@/routes';
import { PlanService } from '@/services/plan';
import { IPlanFilter, IPlanPage } from '@/types/plan';
import { showError } from '@/utils/toast';
import { Box, Flex, Spinner, Stack, Text } from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { LuPlus } from 'react-icons/lu';
import { useNavigate } from 'react-router-dom';

function PlanPage() {
  const navigate = useNavigate();
  // Breadcrumbs
  setBreadcrumbs(ROUTE_PATH.PLAN, [
    { name: trans('menu.plan'), path: ROUTE_PATH.PLAN },
    { name: trans('menu.planList') },
  ]);

  const sv = new PlanService();

  const initialQuery: IPlanFilter = {
    minimal: true,
    page: 1,
    size: 14,
  };

  const [state, setState] = useState<IPlanPage>({
    data: [],
    loading: false,
    query: initialQuery,
  });

  const [totalPages, setTotalPages] = useState<number>(0);
  const queryRef = useRef<IPlanFilter>(initialQuery);

  const handleFetchData = async (params: IPlanFilter, append = false) => {
    try {
      setState((prev) => ({ ...prev, loading: true }));
      queryRef.current = params;
      const res = await sv.getAll(params);
      const newData = res?.plans || [];

      if (res?.totalPages !== undefined) {
        setTotalPages(res.totalPages);
      }

      setState((prev) => ({
        ...prev,
        data: append ? [...prev.data, ...newData] : newData,
        query: params,
        loading: false,
      }));
    } catch (error) {
      showError(error);
      setState((prev) => ({ ...prev, loading: false }));
    }
  };

  const containerRef = useInfiniteScroll({
    loading: state.loading,
    hasMore: totalPages > 0 && (queryRef.current.page || 1) < totalPages,
    onLoadMore: () => {
      if (state.loading) return;

      const currentPage = queryRef.current.page || 1;

      if (totalPages > 0 && currentPage >= totalPages) return;

      const nextPage = currentPage + 1;
      const nextQuery = {
        ...queryRef.current,
        page: nextPage,
      };

      handleFetchData(nextQuery, true);
    },
  });

  const handleCreateNewPlan = () => {
    navigate(ROUTE_PATH.PLAN_CREATE);
  };

  const handleViewDetail = (id: string) => {
    navigate(`${ROUTE_PATH.PLAN}/${id}`);
  };

  useEffect(() => {
    handleFetchData(
      {
        page: 1,
        size: 14,
      },
      false
    );
  }, []);

  return (
    <>
      <Box
        ref={containerRef}
        minH="50vh"
        maxH="85vh"
        width="full"
        bg="white"
        px={'21px'}
        py={'30px'}
        borderRadius={15}
        overflowY="auto"
      >
        <Stack direction="column" gap={4}>
          <Flex flexDirection="column" gap={2}>
            <Text fontSize="18px" fontWeight="bold" color="#2D3748">
              {trans('menu.planPageTitle')}
            </Text>
            <Text fontSize="14px" color="gray.400">
              {trans('menu.planPageSubtitle')}
            </Text>
          </Flex>

          <Flex className='plan-grid' gap={5} flexWrap="wrap">
            {state.data.map((plan) => (
              <Box
                key={plan.id}
                width="370px"
                height="353px"
                bg="white"
                transition="all 0.2s"
                display="flex"
                flexDirection="column"
                cursor="pointer"
                onClick={() => handleViewDetail(plan.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleViewDetail(plan.id);
                  }
                }}
                tabIndex={0}
                role="button"
                aria-label={`View details for ${plan.name}`}
              >
                <Stack direction="column" gap={'20px'} flex={1} justifyContent="space-between">
                  <Box position="relative" borderRadius="12px" height="191.5px" width="100%">
                    <Thumbnail
                      thumbnailPath={plan.thumbnailPath}
                      alt={plan.name}
                      borderRadius="12px"
                      height="191.5px"
                      objectFit="cover"
                      width="100%"
                      showLoading={true}
                    />
                    <Box
                      position="absolute"
                      top={0}
                      left={0}
                      right={0}
                      bottom={0}
                      borderRadius="12px"
                      background="linear-gradient(180deg, rgba(21, 25, 40, 0.88) 0%, rgba(49, 56, 96, 0.16) 100%)"
                    />
                  </Box>
                  <Stack direction="column" gap={0} flex={1} mx={'13px'}>
                    <Text fontSize="10px" color="#A0AEC0" fontWeight="medium">
                      Project #{plan.projectNumber}
                    </Text>
                    <Text
                      fontSize="18px"
                      fontWeight="bold"
                      color="#2D3748"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      whiteSpace="nowrap"
                      maxWidth="262px"
                    >
                      {plan.name}
                    </Text>
                    <Text
                      mt={'10.5px'}
                      w="340px"
                      fontSize="12px"
                      color="gray.400"
                      lineHeight="150%"
                      letterSpacing="0"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      whiteSpace="pre-wrap"
                      lineClamp={2}
                    >
                      {plan.description}
                    </Text>
                  </Stack>
                  <Button
                    mx={'13px'}
                    size="sm"
                    variant="outline"
                    width="110.5px"
                    height="35px"
                    borderColor="#4FD1C5"
                    color="#4FD1C5"
                    fontWeight="bold"
                    fontSize="10px"
                    _hover={{
                      bg: '#4FD1C5',
                      color: 'white',
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewDetail(plan.id);
                    }}
                  >
                    {trans('plan.viewDetail')}
                  </Button>
                </Stack>
              </Box>
            ))}

            <Box
              border="1px solid"
              borderColor="gray.200"
              borderRadius="15px"
              width="370px"
              height="353px"
              bg="white"
              transition="all 0.2s"
              cursor="pointer"
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              onClick={handleCreateNewPlan}
            >
              <Stack direction="column" gap={'11px'} alignItems="center" justifyContent="center">
                <Text
                  w={'30px'}
                  h={'30px'}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  fontSize="40px"
                  color="#718096"
                  lineHeight="100%"
                >
                  <LuPlus />
                </Text>
                <Text fontWeight="bold" fontSize="18px" color="#718096">
                  Create a New Plan
                </Text>
              </Stack>
            </Box>
          </Flex>

          {/* Loading indicator */}
          {state.loading && (
            <Flex justify="center" py={4}>
              <Spinner size="md" color="blue.500" />
            </Flex>
          )}
        </Stack>
      </Box>
    </>
  );
}

export default PlanPage;
